import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Collection } from '../domain/collections';
import { useRepositories } from '../hooks/useRepositories';
import {
  audienceActionsFor,
  leaveCollectionMessage,
  MUTED_COLLECTION_NOTE,
} from '../utils/audienceActions';
import ConfirmDialog from './ConfirmDialog';
import MakePrivateDialog from './MakePrivateDialog';
import { ActionError } from './PageStates';
import { Button } from '@/components/ui/button';
import { Bell, BellOff, Lock, LogOut, Users } from 'lucide-react';

/**
 * The collection page's audience actions: mute or unmute (with a note when
 * it's muted), leave, make private, and the member list. Each is confirmed
 * by the server before the page changes; a refusal leaves it as it was.
 */
export default function CollectionAudienceActions({
  collection,
  onChanged,
}: {
  collection: Collection;
  /** The collection as it is after an action that changed it. */
  onChanged: (collection: Collection) => void;
}) {
  const navigate = useNavigate();
  const { audience } = useRepositories();
  const [muted, setMuted] = useState(collection.isMuted);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [makingPrivate, setMakingPrivate] = useState(false);
  const can = audienceActionsFor(collection);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const mute = () =>
    run(async () => {
      await audience.mute(collection.id);
      setMuted(true);
    });
  const unmute = () =>
    run(async () => {
      await audience.unmute(collection.id);
      setMuted(false);
    });
  const leave = () =>
    run(async () => {
      await audience.leave(collection.id);
      navigate('/my-collections');
    });

  const showMute = can.mute && !muted;
  const showUnmute = can.unmute && muted;
  if (!showMute && !showUnmute && !can.leave && !can.makePrivate && !can.members) return null;

  return (
    <div className="mt-6 space-y-3">
      {muted && (
        <p className="inline-flex items-center gap-2 rounded-full bg-muted px-4 py-2 text-sm font-medium text-muted-foreground">
          <BellOff className="size-4" />
          {MUTED_COLLECTION_NOTE}
        </p>
      )}
      <div className="flex flex-wrap gap-3">
        {showMute && (
          <Button variant="secondary" className="rounded-full" disabled={busy} onClick={mute}>
            <BellOff />
            Mute
          </Button>
        )}
        {showUnmute && (
          <Button variant="secondary" className="rounded-full" disabled={busy} onClick={unmute}>
            <Bell />
            Unmute
          </Button>
        )}
        {can.members && (
          <Button variant="secondary" className="rounded-full" asChild>
            <Link to={`/collections/${collection.id}/members`}>
              <Users />
              Members
            </Link>
          </Button>
        )}
        {can.makePrivate && (
          <Button
            variant="secondary"
            className="rounded-full"
            disabled={busy}
            onClick={() => setMakingPrivate(true)}
          >
            <Lock />
            Make private
          </Button>
        )}
        {can.leave && (
          <Button
            variant="ghost"
            className="rounded-full"
            disabled={busy}
            onClick={() => setConfirmLeave(true)}
          >
            <LogOut />
            Leave
          </Button>
        )}
      </div>

      {error !== null && <ActionError error={error} />}

      <ConfirmDialog
        open={confirmLeave}
        onOpenChange={setConfirmLeave}
        title={`Leave ${collection.name}?`}
        description={<p>{leaveCollectionMessage(collection)}</p>}
        confirmLabel="Leave"
        onConfirm={leave}
      />
      {can.makePrivate && (
        <MakePrivateDialog
          collection={collection}
          open={makingPrivate}
          onOpenChange={setMakingPrivate}
          onDone={onChanged}
        />
      )}
    </div>
  );
}
